import { z } from 'zod'

import { parseLorebook } from '@/features/studio-renew/lib/import-lorebook'
import { IMPORT_FIELDS } from '@/features/studio-renew/lib/studio-import'
import { importLineSchema } from '@/features/studio-renew/schemas/import-result.schema'

import type { ImportBlock } from '@/features/studio-renew/lib/studio-import'
import type { ImportBundle } from '@/features/studio-renew/schemas/import-result.schema'
import type { ImportChoice } from '@/features/studio-renew/services/studio-import.service'

const lines = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
const list = (value: string) =>
  value
    .split(/[,，\n]/)
    .map((line) => line.trim())
    .filter(Boolean)
function textList(value: string, comma = false): string[] {
  // AI가 반환한 여러 줄짜리 한 항목을 행 단위로 쪼개지 않는다.
  try {
    const parsed = z.array(z.string()).safeParse(JSON.parse(value))
    if (parsed.success) return parsed.data
  } catch {
    /* 일반 TXT 원고의 줄 목록을 유지한다. */
  }
  return comma ? list(value) : lines(value)
}
function listText(values: string[], comma = false): string {
  return values.some(
    (value) => value.includes('\n') || (comma && /[,，]/.test(value))
  )
    ? JSON.stringify(values, null, 2)
    : values.join('\n')
}
function array(value: string) {
  if (!value.trim()) return []
  try {
    return z.array(z.record(z.string(), z.unknown())).parse(JSON.parse(value))
  } catch {
    throw new Error('장소·상태 항목의 JSON 배열을 확인해 주세요.')
  }
}
export function blocksToBundle(blocks: ImportBlock[]): ImportBundle {
  const bundle: ImportBundle = {
    work: null,
    world: null,
    status: null,
    characters: [],
    commands: [],
    starts: [],
  }
  for (const block of blocks) {
    const values = { ...block.values }
    const data: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(values)) {
      if (!Object.hasOwn(IMPORT_FIELDS[block.kind], key)) continue
      if (key === 'tags') data[key] = textList(value, true)
      else if (key === 'suggestions' || key === 'forbidden')
        data[key] = textList(value)
      else if (key === 'habits')
        data[key] = textList(value).map((text) => ({ text }))
      else if (key === 'keywords')
        data[key] = value.trim() ? parseLorebook(value) : []
      else if (key === 'design') {
        try {
          data[key] = z.record(z.string(), z.unknown()).parse(JSON.parse(value))
        } catch {
          throw new Error('상태창 디자인 설정의 형식을 확인해 주세요.')
        }
      } else if (key === 'places' || key === 'fields') data[key] = array(value)
      else data[key] = value
    }
    if (block.kind === 'character') bundle.characters.push(data)
    else if (block.kind === 'command') bundle.commands.push(data)
    else if (block.kind === 'start') {
      let dialogue: z.infer<typeof importLineSchema>[] = []
      if (values.dialogue) {
        try {
          dialogue = z
            .array(importLineSchema)
            .parse(JSON.parse(values.dialogue))
        } catch {
          throw new Error('시작 대화의 화자와 내용을 확인해 주세요.')
        }
      } else if (values.greeting?.trim())
        dialogue = [
          {
            who: 'char',
            text: values.greeting,
            characterName: values.speaker ?? '',
          },
        ]
      bundle.starts.push({
        name: values.name ?? '',
        place: values.place ?? '',
        intro: { context: values.context ?? '', lines: dialogue },
        suggestions: textList(values.suggestions ?? ''),
      })
    } else {
      if (bundle[block.kind])
        throw new Error('작품·세계관·상태창은 각각 하나씩 가져올 수 있어요.')
      bundle[block.kind] = data
    }
  }
  return bundle
}

export function choicesToBundle(choices: ImportChoice[]): ImportBundle {
  return blocksToBundle(
    choices
      .filter(
        (choice) =>
          choice.selected && choice.fields.some((field) => field.selected)
      )
      .map((choice) => ({
        key: choice.key,
        kind: choice.kind,
        values: Object.fromEntries(
          choice.fields
            .filter((field) => field.selected)
            .map((field) => [field.key, field.value])
        ),
      }))
  )
}

export function selectedRootCandidate(
  choices: ImportChoice[]
): string | undefined {
  const counts = { character: 0, command: 0 }
  for (const choice of choices.filter(
    (choice) => choice.selected && choice.fields.some((field) => field.selected)
  )) {
    const key =
      choice.kind === 'character' || choice.kind === 'command'
        ? `${choice.kind}:${counts[choice.kind]++}`
        : choice.kind
    if (choice.root) return key
  }
  return undefined
}

export function hasBundleContent(value: unknown): boolean {
  if (typeof value === 'string') return !!value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.some(hasBundleContent)
  return (
    !!value &&
    typeof value === 'object' &&
    Object.values(value).some(hasBundleContent)
  )
}

export function bundleToBlocks(bundle: ImportBundle): ImportBlock[] {
  const blocks: ImportBlock[] = []
  const add = (
    kind: ImportBlock['kind'],
    raw: Record<string, unknown>,
    key: string
  ) => {
    const values: Record<string, string> = {}
    for (const field of Object.keys(IMPORT_FIELDS[kind])) {
      const value = raw[field]
      if (value === undefined || value === null) continue
      if (typeof value === 'string') values[field] = value
      else if (field === 'habits' && Array.isArray(value))
        values[field] = listText(
          value.map((item) =>
            typeof item === 'string'
              ? item
              : z.object({ text: z.string() }).parse(item).text
          )
        )
      else if (
        (field === 'forbidden' ||
          field === 'tags' ||
          field === 'suggestions') &&
        Array.isArray(value)
      )
        values[field] = listText(
          z.array(z.string()).parse(value),
          field === 'tags'
        )
      else values[field] = JSON.stringify(value, null, 2)
    }
    blocks.push({ kind, key, values })
  }
  if (bundle.work) add('work', bundle.work, 'work')
  if (bundle.world) add('world', bundle.world, 'world')
  bundle.characters.forEach((row, index) =>
    add('character', row, `character:${index}`)
  )
  if (bundle.status) add('status', bundle.status, 'status')
  bundle.commands.forEach((row, index) =>
    add('command', row, `command:${index}`)
  )
  bundle.starts.forEach((row, index) =>
    add(
      'start',
      {
        ...row,
        context: row.intro?.context ?? '',
        dialogue: row.intro?.lines ?? [],
      },
      `start:${index}`
    )
  )
  return blocks
}
